import useFlag, { FLAGS } from "@coworker/app/src/hooks/useFlag";
import { User } from "@coworker/apprestructured/src/shared/types/user";
import { StoreTeam } from "@coworker/types/lib/storeteam";
import { useMemo } from "react";
import { useAssignedTeamActiveTasks } from "./hooks/api/useAssignedTeamActiveTasks";
import { useTeamDeletionFlow } from "./hooks/useTeamDeletionFlow";
import { TeamDeletionWizardSteps } from "./hooks/wizard/useTeamDeletionWizard";
import { DeleteTeamConfirmationPromptStep } from "./steps/DeleteTeamConfirmationPromptStep/DeleteTeamConfirmationPromptStep";
import { MoveUsersToTeamStep } from "./steps/MoveUsersToTeamStep/MoveUsersToTeamStep";
import { Team } from "./steps/MoveUsersToTeamStep/components/MoveUsersToTeamSheet/types";
import { TasksStep } from "./steps/TasksStep/TasksStep";
import { BaseStepProps, OperationStepProps } from "./steps/types";

const buildTeamDeletionWizardSteps = ({
  usersStepIsEnabled,
  tasksStepIsEnabled,
}: {
  usersStepIsEnabled: boolean;
  tasksStepIsEnabled: boolean;
}): TeamDeletionWizardSteps[] => {
  const steps: TeamDeletionWizardSteps[] = [
    TeamDeletionWizardSteps.DELETE_TEAM_CONFIRMATION,
  ];

  if (usersStepIsEnabled) {
    steps.push(TeamDeletionWizardSteps.USERS);
  }

  if (tasksStepIsEnabled) {
    steps.push(TeamDeletionWizardSteps.TASKS);
  }

  steps.push(TeamDeletionWizardSteps.COMPLETION);

  return steps;
};

type TeamDeletionWizardProps = {
  teamToDelete: StoreTeam;
  teamToDeleteUsers: User[];
  isDeleting: boolean;
  storeTeams: StoreTeam[] | undefined;
  onDeleteTeam: () => Promise<void>;
  onClose: () => void;
};

export const TeamDeletionWizard = ({
  teamToDelete,
  teamToDeleteUsers,
  isDeleting,
  storeTeams,
  onDeleteTeam,
  onClose,
}: TeamDeletionWizardProps) => {
  const { tasks, isLoading: tasksAreLoading } = useAssignedTeamActiveTasks(
    teamToDelete.id
  );

  const eligibleReassignmentTeams = useMemo(
    () =>
      (storeTeams || [])
        .filter(({ id }) => id !== teamToDelete.id)
        .map<Team>(({ id, name, user_count, storeId }) => ({
          id,
          name,
          memberCount: user_count,
          storeId,
        })),
    [storeTeams, teamToDelete.id]
  );

  const enableTeamDeleteTaskHandling = useFlag(
    FLAGS.ENABLE_TEAM_DELETE_TASK_HANDLING
  );
  const enableTeamUsersHandling = useFlag(
    FLAGS.ENABLE_TEAM_DELETE_USERS_HANDLING
  );
  const wizardSteps = useMemo(
    () =>
      buildTeamDeletionWizardSteps({
        usersStepIsEnabled:
          enableTeamUsersHandling && teamToDeleteUsers.length > 0,
        tasksStepIsEnabled: enableTeamDeleteTaskHandling && tasks.length > 0,
      }),
    [
      teamToDeleteUsers.length,
      tasks.length,
      enableTeamDeleteTaskHandling,
      enableTeamUsersHandling,
    ]
  );

  const { wizard, operations } = useTeamDeletionFlow({
    wizardSteps,
    onDeleteTeam,
    onFlowComplete: onClose,
  });

  const baseStepProps: BaseStepProps = {
    goToNextStep: wizard.goToNextStep,
    goToPreviousStep: wizard.goToPreviousStep,
  };

  const operationStepProps: OperationStepProps = {
    ...baseStepProps,
    teamDeletionFlowOperations: operations,
  };

  return (
    <>
      <DeleteTeamConfirmationPromptStep
        {...baseStepProps}
        visible={
          wizard.currentStep ===
          TeamDeletionWizardSteps.DELETE_TEAM_CONFIRMATION
        }
        team={teamToDelete}
        isDeleting={isDeleting || tasksAreLoading}
        onClose={onClose}
      />

      <MoveUsersToTeamStep
        {...operationStepProps}
        users={teamToDeleteUsers}
        visible={wizard.currentStep === TeamDeletionWizardSteps.USERS}
        teams={eligibleReassignmentTeams}
      />

      <TasksStep
        {...operationStepProps}
        visible={wizard.currentStep === TeamDeletionWizardSteps.TASKS}
        team={teamToDelete}
        tasks={tasks}
      />
    </>
  );
};
